import { FC } from "react";
import SmartTable from "@components/Misc/SmartTable";
import DialogModal from "@components/Misc/DialogModal";
import DefaultInput from "@components/Forms/Controls/Input/DefaultInput";
import ActionBtn from "@components/Forms/Controls/ActionBtn";
import DropdownMenu from "@components/Forms/Controls/DropdownMenu";
import { isArrayWithItems } from "@utils";
import PreloaderLine from "@components/Misc/Preloader/Line";
import "./styles.scss";
import { Icon } from "semantic-ui-react";
import ClipboardCopyButton from "@components/Feedback/ClipboardCopyButton";
import { min } from "moment-timezone";

interface ApiKey {
  id: number;
  name: string;
  api_key?: string;
  key_prefix: string;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface ApiKeyFormData {
  name: string;
  expiryDate: string | null;
}

interface ExpiryOption {
  key: string;
  value: string;
  text: string;
}

interface LoadingState {
  loading: boolean;
  silent: boolean;
}

interface ApiKeyManagementModalViewProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  apiKeys: ApiKey[];
  columns: any; // TODO REWRITE THIS PROPERLY
  loading: LoadingState;
  formData: ApiKeyFormData;
  handleFormChange: (field: keyof ApiKeyFormData, value: string) => void;
  handleCreateKey: () => Promise<void>;
  createLoading: boolean;
  expiryOptions: ExpiryOption[];
  handleRevokeKey: (keyId: number) => Promise<void>;
  itemsLoading: number[];
  showSuccessMessage: boolean;
  setShowSuccessMessage: (show: boolean) => void;
  newKeyData: ApiKey | null;
}

const ApiKeyManagementModalView: FC<ApiKeyManagementModalViewProps> = ({
  isOpen,
  setOpen,
  apiKeys,
  columns,
  loading,
  formData,
  handleFormChange,
  handleCreateKey,
  createLoading,
  expiryOptions,
  showSuccessMessage,
  newKeyData,
}) => {
  return (
    <DialogModal
      isOpen={isOpen}
      setOpen={setOpen}
      disallowAutoFocus={true}
      disallowClickoff={true}
      isButtonClose={true}
      style={{ width: "90%", maxWidth: "1200px", overflow: "visible" }}
    >
      <div className="api-key-management__wrapper">
        <div>
          <h2>API Key Management</h2>
          <p className="api-key-management__description">
            Create and manage API keys for programmatic access.
          </p>
        </div>

        {/* Create API Key Form */}
        <div className="api-key-management__form-wrapper">
          <div>
            <h3 className="api-key-management__form-title">
              Create New API Key
            </h3>
            <div className="api-key-management__form-grid">
              <div className="api-key-management__form-inputs">
                <DefaultInput
                  label="API Key Name"
                  onChange={(value: any) => handleFormChange("name", value)}
                  value={formData.name}
                  type="text"
                  placeholder="e.g. Production API Key"
                  name="api-key-name"
                  isDisabled={false}
                  inputStyle={{}}
                  min={undefined}
                  max={undefined}
                />
                <div>
                  <label className="api-key-management__form-label">
                    Expiry
                  </label>
                  <DropdownMenu
                    size={"small"}
                    style={{ padding: "0px", minWidth: "150px" }}
                    items={expiryOptions}
                    defaultCustomProject={{
                      value: [formData.expiryDate || "never"],
                    }}
                    selectOption={(value: any) =>
                      handleFormChange("expiryDate", value)
                    }
                  />
                </div>
              </div>
              <div className="api-key-management__form-button">
                <ActionBtn
                  loading={{ loading: createLoading }}
                  callback={handleCreateKey}
                  label="Create API Key"
                  icon="add"
                  isDisabled={!formData.name.trim()}
                />
              </div>
            </div>
          </div>

          {/* Success Message */}
          {showSuccessMessage && newKeyData && (
            <div className="api-key-management__success-message">
              <div className="api-key-management__success-info">
                <Icon name="info circle" />
                <p>
                  Make sure to copy this API key now—once you close this modal,
                  it won’t be accessible again.
                </p>
              </div>

              <div className="api-key-management__success-fields">
                <div className="api-key-management__success-field">
                  <label>API Key Name</label>
                  <span>{newKeyData.name}</span>
                </div>

                <div className="api-key-management__success-field">
                  <label>API Key</label>
                  <span>
                    {newKeyData.api_key}{" "}
                    <ClipboardCopyButton value={newKeyData.api_key || ""} />
                  </span>
                </div>
                <div className="api-key-management__success-field">
                  <label>Expires</label>
                  <span>
                    {newKeyData.expires_at
                      ? new Date(newKeyData.expires_at).toLocaleDateString()
                      : "Never"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* API Keys Table */}
        <div className="api-key-management__table-section">
          <span className="api-key-management__table-header">
            {apiKeys.length} Active Key{apiKeys.length !== 1 ? "s" : ""}
          </span>
          {loading.loading && !loading.silent ? (
            <div className="list-table">
              <div className="api-key-management__table-preloader">
                <PreloaderLine sections={4} size={"med"} />
                {[...Array(5)].map((_, index) => (
                  <PreloaderLine sections={4} size={"small"} key={index} />
                ))}
              </div>
            </div>
          ) : (
            <>
              {isArrayWithItems(apiKeys) && isArrayWithItems(columns) ? (
                <SmartTable
                  getSortedState={() => {}}
                  columns={columns}
                  data={apiKeys}
                  disableScrollToTop={true}
                  hasRowPagination={false}
                  cssStyles={{ maxHeight: "400px" }}
                />
              ) : (
                <div className="list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Key Prefix</th>
                        <th>Last Used</th>
                        <th>Expires</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          colSpan={5}
                          className="api-key-management__table-empty"
                        >
                          No API keys created yet. Create one above to get
                          started.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DialogModal>
  );
};

export default ApiKeyManagementModalView;
